import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import DatePicker from 'components/DatePicker/DatePicker';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import { UID_NAME_TUPLES } from 'constants/dataSets';
import { PROFIT_CENTER_LINKS } from 'constants/formFields';
import { DATE_FORMAT } from 'constants/general';
import { NUMBER_FORMATS } from 'constants/interfaces';

import type { TDate } from 'components/DatePicker/interfaces/TDate';
import type { FieldPath } from 'react-hook-form';
import type { TCreateCustomerSchema } from 'utils/validators';
import type INumberFieldValue from '../interfaces/INumberFieldValue';
import type IProfitCenterItemProps from '../interfaces/IProfitCenterItemProps';
import type ISelectOption from '../interfaces/ISelectOption';

import styles from '../sass/ProfitCenterLinksCustomers.module.scss';

function ProfitCenterItem({
  profitCenter,
  index,
  control,
  recordId,
  placeholder,
  profitCenterIds,
  companyCurrency,
  onRemove,
  updateProfitCenterField,
  showDeleteButton,
}: IProfitCenterItemProps) {
  const { t } = useTranslation();
  const profitCenterId = `profit-center-${profitCenter.profitCenterId || index}`;

  return (
    <div key={profitCenterId} className={styles.profitCenterItem}>
      <h3>
        {t('common.profitCentre', { number: index + 1 })}
      </h3>

      {showDeleteButton ? (
        <button
          type="button"
          className={styles.deleteButton}
          onClick={() => onRemove(index)}
          aria-label={t('common.delete')}
        >
          <TrashIcon />
        </button>
      ) : null}

      <div className={`${styles.formRow} ${styles.formRowHalf}`}>
        <Controller
          control={control}
          name={`${PROFIT_CENTER_LINKS}.${index}.profitCenterId` as FieldPath<TCreateCustomerSchema>}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              label={t('customers.fields.profitCentre')}
              placeholder={recordId ? placeholder : t('customers.fields.profitCentre')}
              options={profitCenterIds?.[UID_NAME_TUPLES.profitCenter]}
              value={value ? { value, label: value } : null}
              onChange={(newValue: unknown) => {
                const selectedOption = newValue as ISelectOption | null;
                onChange(selectedOption?.value || '');
                updateProfitCenterField(index, 'profitCenterId', selectedOption?.value || '');
              }}
              error={error?.message}
              menuPosition="absolute"
              isSearchable
              required
            />
          )}
        />
      </div>

      <div className={styles.formRow}>
        <Controller
          control={control}
          name={`${PROFIT_CENTER_LINKS}.${index}.startDate` as FieldPath<TCreateCustomerSchema>}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <DatePicker
              name={name}
              label={t('customers.fields.startDate')}
              error={error?.message}
              onChange={(date: TDate) => {
                onChange(date);
                updateProfitCenterField(index, 'startDate', date);
              }}
              value={value}
              placeholder={placeholder || DATE_FORMAT}
              required
              maxDate={new Date()}
            />
          )}
        />

        <Controller
          control={control}
          name={`${PROFIT_CENTER_LINKS}.${index}.endDate` as FieldPath<TCreateCustomerSchema>}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <DatePicker
              name={name}
              label={t('customers.fields.endDate')}
              error={error?.message}
              onChange={(date: TDate) => {
                onChange(date);
                updateProfitCenterField(index, 'endDate', date);
              }}
              value={value}
              minDate={profitCenter.startDate ? new Date(profitCenter.startDate) : undefined}
              placeholder={placeholder || DATE_FORMAT}
              required
            />
          )}
        />
      </div>

      <div className={styles.formRow}>
        <Controller
          control={control}
          name={`${PROFIT_CENTER_LINKS}.${index}.totalCost` as FieldPath<TCreateCustomerSchema>}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <div className={styles.totalCostContainer}>
              <div className={styles.fieldWithCurrency}>
                <NumberField
                  name={name}
                  label={t('customers.fields.actualRevenue')}
                  value={value}
                  error={error?.message}
                  onChange={(values: INumberFieldValue) => {
                    onChange(values.value);
                    updateProfitCenterField(index, 'totalCost', values.value);
                  }}
                  placeholder={placeholder || t('customers.fields.actualRevenue')}
                  className={styles.valueInput}
                  allowNegative={false}
                  hideTextError
                  required
                />
                <span className={`${styles.currencyInput} ${error?.message ? styles.errorBorder : ''}`}>
                  M
                  {' '}
                  {companyCurrency}
                </span>
              </div>
              {error?.message ? <span className={styles.errorMessage}>{error.message}</span> : null}
            </div>
          )}
        />

        <Controller
          control={control}
          name={`${PROFIT_CENTER_LINKS}.${index}.percentOfTotalCost` as FieldPath<TCreateCustomerSchema>}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <NumberField
              name={name}
              label={t('customers.fields.totalRevenue')}
              value={value}
              error={error?.message}
              onChange={(values: INumberFieldValue) => {
                onChange(values.value);
                updateProfitCenterField(index, 'percentOfTotalCost', values.value);
              }}
              placeholder={placeholder || t('customers.fields.totalRevenue')}
              type={NUMBER_FORMATS.percent}
              required
            />
          )}
        />
      </div>
    </div>
  );
}

export default ProfitCenterItem;
